import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { MathRenderer } from '../math/MathRenderer';

const QuestionCard = ({ question, onSubmit, stemText, stemImage, progress = null }) => {
  const [selectedOptionIds, setSelectedOptionIds] = useState([]);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  // Shuffle options only once per question
  const shuffledOptions = useMemo(() => {
    const options = [...question.options];
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    return options;
  }, [question.id]); // Re-shuffle when question changes

  const handleOptionToggle = (optionId) => {
    setSelectedOptionIds(prev =>
      prev.includes(optionId)
        ? prev.filter(id => id !== optionId)
        : [...prev, optionId]
    );
  };

  const handleSubmit = () => {
    const correct = 
      selectedOptionIds.length === question.correctOptionIds.length &&
      selectedOptionIds.every(id => question.correctOptionIds.includes(id));
    
    setIsCorrect(correct);
    setIsSubmitted(true);
  };

  const handleNext = () => {
    // Call onSubmit to trigger background progress update
    onSubmit(isCorrect);
    
    // Reset state for next question
    setSelectedOptionIds([]);
    setIsSubmitted(false);
    setIsCorrect(false);
  };

  const getOptionClasses = (optionId) => {
    if (!isSubmitted) {
      return selectedOptionIds.includes(optionId)
        ? 'border-indigo-500 bg-indigo-900/30'
        : 'border-slate-600 bg-slate-700 hover:border-slate-500';
    }
    
    // After submission - only show correct answers if user was correct
    if (isCorrect) {
      const isCorrectOption = question.correctOptionIds.includes(optionId);
      if (isCorrectOption) {
        return 'border-green-500 bg-green-900/30';
      }
    }
    
    // If user was wrong, don't highlight anything
    return 'border-slate-600 bg-slate-700';
  };

  return (
    <div className="bg-slate-800 p-2 rounded-lg shadow-xl">
      {stemText && (
        <div className="mb-6 p-4 bg-slate-700/50 rounded-md border border-slate-600">
          <h4 className="text-sm font-bold text-indigo-400 mb-2">STEM PROMPT:</h4>
          <div className="prose prose-invert max-w-none">
            <MathRenderer content={stemText} enableMarkdown={true} enableMath={true} />
          </div>
          <div className="flex justify-center">
            {stemImage && (
              <img src={stemImage} alt="Stem reference" className="mt-3 rounded-md"/>
            )}
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="prose prose-invert max-w-none">
          <MathRenderer content={question.text} enableMarkdown={true} enableMath={true} />
        </div>
      </div>
      
      {progress && (
        <p className="text-center text-sm text-slate-400 mb-6">
          Sub-question {progress.current} of {progress.total}
        </p>
      )}
      
      <div className="space-y-3 my-8">
        {shuffledOptions.map(option => (
          <label 
            key={option.id} 
            className={`flex items-center p-4 rounded-md border transition-all duration-200 cursor-pointer ${getOptionClasses(option.id)}`}
          >
            <input 
              type="checkbox" 
              checked={selectedOptionIds.includes(option.id)} 
              onChange={() => handleOptionToggle(option.id)} 
              disabled={isSubmitted} 
              className="h-5 w-5 rounded bg-slate-600 border-slate-500 text-indigo-500 focus:ring-indigo-600 mr-4 disabled:opacity-50"
            />
            <span className="text-slate-200 flex-grow">
              <div className="prose prose-invert max-w-none">
                <MathRenderer content={option.text} enableMarkdown={false} enableMath={true} />
              </div>
            </span>
          </label>
        ))}
      </div>
      
      {!isSubmitted ? (
        <button 
          onClick={handleSubmit} 
          disabled={selectedOptionIds.length === 0} 
          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-4 rounded-md transition disabled:bg-slate-600 disabled:cursor-not-allowed"
        >
          Submit Answer
        </button>
      ) : (
        <div className="text-center">
          <p className={`text-xl font-bold mb-4 ${isCorrect ? 'text-green-400' : 'text-red-400'}`}>
            {isCorrect ? 'Correct!' : 'Incorrect.'}
          </p>
          <button 
            onClick={handleNext} 
            className="w-full bg-slate-600 hover:bg-slate-500 text-white font-bold py-3 px-4 rounded-md transition"
          >
            Next &rarr;
          </button>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;